import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";

import { saveDraft } from "@RequestFormV2/api/draftApi";
import { useRequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { useRequestFormDraftId } from "@RequestFormV2/hooks/useRequestFormDraftId";
import { getTopLevelDraftKey } from "@RequestFormV2/utils/getTopLevelDraftKey";
import { prepareDraftPayload } from "@RequestFormV2/utils/prepareDraftPayload";
import { shouldInvalidateProviderOptions } from "@RequestFormV2/utils/shouldInvalidateProviderOptions";

import { RequestFormSaveContext } from "./RequestFormSaveContext";

const DRAFT_SAVE_DEBOUNCE_MS = 800;
const INVITED_MODES_DEBOUNCE_MS = 1000;

type RequestFormSaveProviderProps = {
  children: React.ReactNode;
};

export function RequestFormSaveProvider({ children }: RequestFormSaveProviderProps) {
  const draftId = useRequestFormDraftId();
  const mode = useRequestFormMode();
  const queryClient = useQueryClient();
  const { getValues, setValue } = useFormContext();
  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invitedModesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTopLevelKeysRef = useRef<Set<string>>(new Set());
  const pendingPathsRef = useRef<string[]>([]);
  const [isDraftSaving, setIsDraftSaving] = useState(false);

  const performSave = useCallback(async (): Promise<boolean> => {
    if (!draftId) return false;

    const location = getValues("organizational.location");
    if (!location) return false;

    const keys = [...pendingTopLevelKeysRef.current];
    if (keys.length === 0) return false;

    const pathsToInvalidate = [...pendingPathsRef.current];

    setIsDraftSaving(true);
    try {
      // Quick legacy PUT replaces the entire draft; Quote PATCH deep-merges partial keys.
      const payload =
        mode === "quick"
          ? prepareDraftPayload(getValues(), undefined, mode)
          : prepareDraftPayload(getValues(), keys, mode);
      if (Object.keys(payload).length === 0) {
        return false;
      }

      await saveDraft(draftId, payload, mode);
      setValue("draftSaved", true, { shouldDirty: false });

      if (pathsToInvalidate.some((path) => shouldInvalidateProviderOptions(path))) {
        await queryClient.invalidateQueries({
          queryKey: ["draft-provider-options", "list", draftId],
        });
      }

      pendingTopLevelKeysRef.current = new Set();
      pendingPathsRef.current = [];
      return true;
    } catch {
      setValue("draftSaved", false, { shouldDirty: false });
      return false;
    } finally {
      setIsDraftSaving(false);
    }
  }, [draftId, getValues, mode, queryClient, setValue]);

  const performFullSave = useCallback(async (): Promise<boolean> => {
    if (!draftId) return false;

    const location = getValues("organizational.location");
    if (!location) return false;

    setIsDraftSaving(true);
    try {
      const payload = prepareDraftPayload(getValues(), undefined, mode);
      if (Object.keys(payload).length === 0) {
        return false;
      }

      await saveDraft(draftId, payload, mode);
      setValue("draftSaved", true, { shouldDirty: false });
      await queryClient.invalidateQueries({
        queryKey: ["draft-provider-options", "list", draftId],
      });

      pendingTopLevelKeysRef.current = new Set();
      pendingPathsRef.current = [];
      return true;
    } catch {
      setValue("draftSaved", false, { shouldDirty: false });
      return false;
    } finally {
      setIsDraftSaving(false);
    }
  }, [draftId, getValues, mode, queryClient, setValue]);

  const queueDraftSave = useCallback(
    (path?: string) => {
      if (!draftId || !getValues("organizational.location")) return;

      if (path) {
        const topLevelKey = getTopLevelDraftKey(path);
        if (topLevelKey) {
          pendingTopLevelKeysRef.current.add(topLevelKey);
        }
        pendingPathsRef.current.push(path);
      }

      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }

      draftSaveTimerRef.current = setTimeout(() => {
        draftSaveTimerRef.current = null;
        performSave().catch(() => undefined);
      }, DRAFT_SAVE_DEBOUNCE_MS);
    },
    [draftId, getValues, performSave],
  );

  const flushSave = useCallback(async () => {
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }

    return performFullSave();
  }, [performFullSave]);

  const saveField = useCallback(
    async (
      path: string,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _value: unknown,
    ) => {
      queueDraftSave(path);
    },
    [queueDraftSave],
  );

  const saveInvitedModes = useCallback(() => {
    if (invitedModesTimerRef.current) {
      clearTimeout(invitedModesTimerRef.current);
    }

    invitedModesTimerRef.current = setTimeout(() => {
      queueDraftSave("invited_modes");
    }, INVITED_MODES_DEBOUNCE_MS);
  }, [queueDraftSave]);

  useEffect(
    () => () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
      if (invitedModesTimerRef.current) {
        clearTimeout(invitedModesTimerRef.current);
      }
    },
    [],
  );

  const value = useMemo(
    () => ({ saveField, saveInvitedModes, queueDraftSave, flushSave, isDraftSaving }),
    [saveField, saveInvitedModes, queueDraftSave, flushSave, isDraftSaving],
  );

  return <RequestFormSaveContext.Provider value={value}>{children}</RequestFormSaveContext.Provider>;
}
